import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchResultQuery } from '../queries/useSearchResultQuery';
import { useSearchResultMetaQuery } from '../queries/useSearchResultMetaQuery';
import { Flex, Spinner, useScreenSize } from '@banhala/ui-kit';
import { useScrollToFilterList } from 'screens/src/components/hooks/useScrollToFilterList';
import { AtomicRenderer, useFilterListAtom } from 'shared/src/component';
import { useLogViewEvent } from '../hook/useLogViewEvent';
import { useIsForegroundNow } from 'shared/src/hooks';
import { useScrollRestoration } from 'shared/src/hooks/useScrollRestoration';
import { retainedState } from 'shared/src/hooks/retainedState';
import { scheduleOnRN } from 'react-native-worklets';
import type { ListRef } from '@banhala/ui-kit';
import type { IScreensComponent } from 'shared/src/api';

// visit(retainedState) 단위 스크롤 위치 슬롯. 가려진 화면 unmount→remount 를
// 가로질러 검색 결과 리스트의 스크롤 위치를 보존한다.
const scrollOffsetSlot = retainedState(0);

type SearchResultData = NonNullable<
  ReturnType<typeof useSearchResultQuery>['data']
>;

type SearchResultListProps = {
  screenName: string;
  query: string;
  nextToken?: string;
  onScrollY?: (scrollY: number) => void;
  onScrollEndWorklet?: (scrollY: number) => void;
};

/**
 * 검색 결과 상품 리스트
 * - SearchResult 페이지 전용
 * - 검색어 기반 무한 스크롤 + 필터 연동
 *
 * 데이터 로딩(useSearchResultQuery: staleTime/gcTime 0 → visit 복귀마다 풀 로딩) 동안에는
 * 리스트(AtomicRenderer)가 아예 마운트되지 않는다. 그래서 스크롤 복원 로직
 * (useScrollRestoration)을 데이터 준비 후에만 마운트되는 SearchResultListContent 로
 * 분리한다. 이렇게 해야 복원 훅의 백스톱/onContentSizeChange 가 살아있는 listRef 를
 * 상대로 동작한다. (GoodsDetailContents 와 동일하게 "리스트와 함께 마운트")
 */
export const SearchResultList = ({
  screenName,
  query,
  nextToken,
  onScrollY,
  onScrollEndWorklet,
}: SearchResultListProps) => {
  const isForeground = useIsForegroundNow();

  const {
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    refetch,
  } = useSearchResultQuery({
    screenName,
    query,
    nextToken,
  });

  // iOS `MetaFeedReactor.fetchScreenAppreanceData` / Android `ComponentMetaViewModel`
  // 와 1:1 대칭. SEARCH_RESULT는 서버가 "3col"로 내려줘 3col 스켈레톤을 표시.
  const { data: skeletonType } = useSearchResultMetaQuery(screenName);

  // 지연 발화 대기 중 자동 prefetch — iOS의 willDisplay 기반 auto-loadmore와 동일한 의도
  // (VIEW 이벤트 유실 방지). prefetch 조건 자체는 select에서 계산됨.
  useEffect(() => {
    if (data?.shouldAutoPrefetchNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [data?.shouldAutoPrefetchNextPage, isFetchingNextPage, fetchNextPage]);

  // iOS: MetaFeedReactor.trackViewLoggingEvent → VIEW_{screenName}
  useLogViewEvent(data);

  if (isLoading) {
    return (
      <Flex.Center flex={1}>
        <Spinner size={20} />
      </Flex.Center>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <SearchResultListContent
      data={data}
      query={query}
      isFetching={isFetching}
      fetchNextPage={fetchNextPage}
      refetch={refetch}
      skeletonType={skeletonType}
      isForeground={isForeground}
      onScrollY={onScrollY}
      onScrollEndWorklet={onScrollEndWorklet}
    />
  );
};

type SearchResultListContentProps = {
  data: SearchResultData;
  query: string;
  isFetching: boolean;
  fetchNextPage: VoidFunction;
  refetch: VoidFunction;
  skeletonType: ReturnType<typeof useSearchResultMetaQuery>['data'];
  isForeground: boolean;
  onScrollY?: (scrollY: number) => void;
  onScrollEndWorklet?: (scrollY: number) => void;
};

/**
 * 데이터가 준비된 뒤에만 마운트되는 실제 리스트.
 * listRef / useScrollRestoration 이 여기서 리스트와 함께 마운트되므로, 복원이 호출될 때
 * listRef.current 가 항상 살아있다.
 */
const SearchResultListContent = ({
  data,
  query,
  isFetching,
  fetchNextPage,
  refetch,
  skeletonType,
  isForeground,
  onScrollY,
  onScrollEndWorklet,
}: SearchResultListContentProps) => {
  const listRef = useRef<ListRef<IScreensComponent> | null>(null);

  // 필터/검색어 변경 시 AtomicRenderer의 displayHistoryRef 캐시 reset 트리거.
  // - 필터 적용: filterListAtomValue 객체 reference 변경
  // - 자동완성/연관검색어 inline 클릭(같은 인스턴스 내 query 변경): query 변경
  // - 재검색(close→open): 새 인스턴스로 자연 mount/unmount
  const { filterListAtomValue } = useFilterListAtom();
  const filterParams = useMemo(
    () => ({ ...filterListAtomValue, query }),
    [filterListAtomValue, query],
  );

  const itemHeightsRef = useRef<{ [key: number]: number }>({});
  const {
    filterListIndex,
    scrollToFilterList,
    onItemHeightChange,
    isPendingScroll,
  } = useScrollToFilterList({
    atomicRendererRef: listRef,
    components: data.components,
    isFetching,
    itemHeightsRef,
  });

  // 스크롤 위치 저장/복원 (visit 단위). 가려진 화면 unmount→remount 시 위치 보존.
  // 이 컴포넌트는 데이터 준비 후 listRef 와 함께 마운트되므로 복원 시 ref 가 살아있다.
  const { height: screenHeight } = useScreenSize();
  const scrollToOffset = useCallback((offset: number, animated: boolean) => {
    listRef.current?.scrollToOffset({ offset, animated });
  }, []);
  const scrollRestoration = useScrollRestoration({
    token: scrollOffsetSlot,
    scrollTo: scrollToOffset,
    viewportHeight: screenHeight,
  });

  // 스크롤 종료(onEndDrag / onMomentumEnd) 시점에 현재 offset 저장 + 기존 worklet 핸들러 위임.
  const handleScrollEndWorklet = useCallback(
    (y: number) => {
      'worklet';
      scheduleOnRN(scrollRestoration.save, y);
      if (onScrollEndWorklet) {
        onScrollEndWorklet(y);
      }
    },
    [scrollRestoration.save, onScrollEndWorklet],
  );

  return (
    <>
      {/* EXP1592 클릭 지표 검증: iOS dimmedCover 정합으로 넣었던 dim 배경 + 터치
          차단(pointerEvents auto) 오버레이가 CLICK_GOODS 드랍 원인인지 확인하기 위해
          의도적으로 비차단 스피너만 유지(iOS 미정합 상태). pointerEvents none이므로
          로딩 중에도 스크롤/클릭이 모두 가능하다. */}
      {isFetching && (
        <Flex.Center
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
          pointerEvents="none"
          zIndex={1000}
        >
          <Spinner size={20} color="content_tertiary" />
        </Flex.Center>
      )}
      <AtomicRenderer
        refetch={refetch}
        data={data}
        fetchNextPage={fetchNextPage}
        listRef={listRef}
        filterListIndex={filterListIndex}
        scrollToFilterList={scrollToFilterList}
        isFetching={isFetching}
        isPendingScroll={isPendingScroll}
        filterParams={filterParams}
        itemHeightsRef={itemHeightsRef}
        onItemHeightChange={onItemHeightChange}
        onScrollY={onScrollY}
        skeletonType={skeletonType}
        onScrollEndWorklet={handleScrollEndWorklet}
        isForeground={isForeground}
        onContentSizeChange={scrollRestoration.onContentSizeChange}
        listStyle={scrollRestoration.restoring ? { opacity: 0 } : undefined}
      />
    </>
  );
};
